import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader, StatusBadge } from "@/components/PageHeader";
import { api } from "@/lib/api";
import { fmtDate, fmtRelative } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Clock, Send, ListTodo, Users, MailWarning, Zap, Receipt } from "lucide-react";
import { toast } from "sonner";

function Metric({ label, value, tone, icon: Icon, testid }) {
  const toneMap = {
    danger: "text-rose-600",
    warn: "text-amber-600",
    ok: "text-emerald-600",
    info: "text-blue-600",
    neutral: "text-slate-900",
  };
  return (
    <div data-testid={testid} className="border border-slate-200 rounded-md bg-white p-5">
      <div className="flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-[0.14em] font-semibold text-slate-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${toneMap[tone] || toneMap.neutral}`} />}
      </div>
      <div className={`mt-3 font-mono text-3xl font-semibold ${toneMap[tone] || toneMap.neutral}`}>{value ?? "—"}</div>
    </div>
  );
}

export default function Dashboard() {
  const [summary, setSummary] = useState(null);
  const [invSummary, setInvSummary] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => Promise.all([
    api.get("/dashboard/summary").then((r) => setSummary(r.data)),
    api.get("/invoices/summary").then((r) => setInvSummary(r.data)).catch(() => setInvSummary(null)),
  ]);

  useEffect(() => { load(); }, []);

  const runEngine = async () => {
    setBusy(true);
    try {
      const r = await api.post("/reminders/run-due");
      toast.success(`Reminder engine ran — ${r.data.sent} sent, ${r.data.blocked} blocked`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    } finally {
      setBusy(false);
    }
  };

  const c = summary?.counts || {};

  return (
    <div className="flex flex-col">
      <PageHeader
        testid="page-dashboard"
        title="Dashboard"
        subtitle="Today at a glance · Europe/London"
        actions={
          <Button
            data-testid="run-engine-btn"
            variant="outline"
            onClick={runEngine}
            disabled={busy}
            className="rounded-md"
          >
            <Zap className="h-4 w-4 mr-2" /> Run reminder engine
          </Button>
        }
      />

      <div className="p-6 lg:p-8 space-y-6">
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
          <Metric testid="metric-overdue" label="Overdue" value={c.overdue} tone="danger" icon={AlertTriangle} />
          <Metric testid="metric-upcoming" label="Due in 14 days" value={c.upcoming_14d} tone="warn" icon={Clock} />
          <Metric testid="metric-pending" label="Pending approval" value={c.pending_reminders} tone="warn" icon={MailWarning} />
          <Metric testid="metric-approved" label="Approved" value={c.approved_reminders} tone="info" icon={Send} />
          <Metric testid="metric-sent-today" label="Sent today" value={c.sent_today} tone="ok" icon={Send} />
          <Metric testid="metric-open-tasks" label="Open tasks" value={c.open_tasks} tone="neutral" icon={ListTodo} />
          <Metric testid="metric-clients" label="Active clients" value={c.active_clients} tone="neutral" icon={Users} />
          <Link to="/invoices" className="col-span-2 md:col-span-1">
            <Metric
              testid="metric-overdue-invoices"
              label="Overdue invoices"
              value={invSummary ? `${invSummary.overdue_count}${invSummary.overdue_count ? ` · £${Number(invSummary.overdue_amount || 0).toLocaleString("en-GB", {minimumFractionDigits: 2, maximumFractionDigits: 2})}` : ""}` : "—"}
              tone={invSummary && invSummary.overdue_count > 0 ? "danger" : "neutral"}
              icon={Receipt}
            />
          </Link>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <section data-testid="overdue-list" className="border border-slate-200 rounded-md bg-white">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-base font-semibold text-slate-900">Overdue jobs</h2>
                <div className="text-xs text-slate-500 mt-0.5">Requires immediate attention</div>
              </div>
              <StatusBadge status="overdue" label={`${c.overdue || 0} overdue`} />
            </div>
            <ul className="divide-y divide-slate-100">
              {(summary?.overdue || []).length === 0 && (
                <li className="px-5 py-8 text-sm text-slate-500 text-center">Nothing overdue — well done.</li>
              )}
              {(summary?.overdue || []).map((j) => (
                <li key={j.job_id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="min-w-0">
                    <Link to={`/clients/${j.client_id}`} className="text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors">
                      {j.title}
                    </Link>
                    <div className="text-xs text-slate-500">{j.client_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xs text-rose-600">{fmtDate(j.deadline)}</div>
                    <div className="text-[11px] text-slate-500">{fmtRelative(j.deadline)}</div>
                  </div>
                </li>
              ))}
            </ul>
          </section>

          <section data-testid="upcoming-list" className="border border-slate-200 rounded-md bg-white">
            <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h2 className="font-heading text-base font-semibold text-slate-900">Upcoming — next 14 days</h2>
                <div className="text-xs text-slate-500 mt-0.5">Plan your week</div>
              </div>
              <StatusBadge status="due_soon" label={`${c.upcoming_14d || 0} due soon`} />
            </div>
            <ul className="divide-y divide-slate-100">
              {(summary?.upcoming || []).length === 0 && (
                <li className="px-5 py-8 text-sm text-slate-500 text-center">Nothing due in the next 14 days.</li>
              )}
              {(summary?.upcoming || []).map((j) => (
                <li key={j.job_id} className="px-5 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors">
                  <div className="min-w-0">
                    <Link to={`/clients/${j.client_id}`} className="text-sm font-medium text-slate-900 hover:text-blue-600 transition-colors">
                      {j.title}
                    </Link>
                    <div className="text-xs text-slate-500">{j.client_name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono text-xs text-slate-900">{fmtDate(j.deadline)}</div>
                    <div className="text-[11px] text-slate-500">{fmtRelative(j.deadline)}</div>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>
      </div>
    </div>
  );
}
